import { useEffect, useMemo } from 'react';
import { EmotionValue } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { createMovieUGC } from '../lib/movieUGC';
import { ToastNotificationViewVariant } from '../lib/popups';

import { useInMemoryCache } from './useInMemoryCache';
import { useGraphqlClient } from './useGraphqlClient';
import { useToast } from './usePopups';
import { useI18n } from './useI18n';
import { useLogger } from './useLogger';

interface TogglePlannedToWatchParams {
    toastViewVariant?: ToastNotificationViewVariant;
}

const toastShowTime = 6;

function isAbortError(value: unknown): value is DOMException {
    return value instanceof DOMException && value.name === 'AbortError';
}

export function useMovieUGC(movieId: number) {
    const inMemoryCache = useInMemoryCache();
    const graphqlClient = useGraphqlClient();
    const toast = useToast();
    const i18n = useI18n();
    const logger = useLogger();

    const ugc = useMemo(() => {
        const abortController = new AbortController();

        const movieUGC = createMovieUGC({
            movieId,
            graphqlClient,
            inMemoryCache,
            abortSignal: abortController.signal,
        });

        return {
            plannedToWatch: movieUGC.plannedToWatch,
            notInterested: movieUGC.notInterested,
            watched: movieUGC.watched,
            emotion: movieUGC.emotion,

            async togglePlannedToWatch({
                toastViewVariant = 'BOTTOM',
            }: TogglePlannedToWatchParams = {}) {
                try {
                    const plannedToWatch = await movieUGC.togglePlannedToWatch();

                    const toastType = plannedToWatch ? 'BOOKMARK' : 'UNBOOKMARK';
                    const toastMessage = plannedToWatch
                        ? i18n('movie_set_planned_to_watch')
                        : i18n('movie_remove_planned_to_watch');

                    toast.show({
                        type: toastType,
                        message: toastMessage,
                        viewVariant: toastViewVariant,
                        showTime: toastShowTime,
                    });
                } catch (error) {
                    if (isAbortError(error)) {
                        return;
                    }

                    logger.error({ error }, 'Failed to toggle planned to watch');
                }
            },

            async toggleNotInterested() {
                try {
                    const notInterested = await movieUGC.toggleNotInterested();

                    const toastType = notInterested ? 'NOTINTERESTED' : 'UNDONOTINTERESTED';
                    const toastMessage = notInterested
                        ? i18n('movie_set_not_interested')
                        : i18n('movie_remove_not_interested');

                    toast.show({
                        type: toastType,
                        message: toastMessage,
                        viewVariant: 'BOTTOM',
                        showTime: toastShowTime,
                    });
                } catch (error) {
                    if (isAbortError(error)) {
                        return;
                    }

                    logger.error({ error }, 'Failed to toggle not interested');
                }
            },

            async toggleWatched() {
                try {
                    await movieUGC.toggleWatched();
                } catch (error) {
                    if (isAbortError(error)) {
                        return;
                    }

                    logger.error({ error }, 'Failed to toggle watched');
                }
            },

            async setEmotion(value: EmotionValue) {
                try {
                    await movieUGC.setEmotion(value);
                } catch (error) {
                    if (isAbortError(error)) {
                        return;
                    }

                    logger.error({ error }, 'Failed to set emotion');
                }
            },

            cleanUp() {
                abortController.abort();
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movieId]);

    useEffect(() => ugc.cleanUp, [ugc]);

    return ugc;
}
